import { useSessionStorage } from "../../../hooks";
import {
  cancelTransaction,
  checkout,
  getExtendedTransactions,
  reportIncident,
} from "@services/krent.service";
import ktoast from "@services/toast.service";
import { PrimaryButton } from "@shared/buttons";
import { isEmpty } from "@utils/helpers";
import { differenceInDays } from "date-fns";
import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import Modal from "react-responsive-modal";

import { Link, useNavigate } from "react-router-dom";
import { toast, ToastContainer } from "react-toastify";
import { formatStatusLabel } from "../../../helpers/statusStyles";

type MenuPosition = {
  top?: number;
  bottom?: number;
  right: number;
};

const SlidePanel = ({
  isOpen,
  onClose,
  children,
}: {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
}) => {
  return (
    <div className={`slide-panel ${isOpen ? "open" : ""}`}>
      <button onClick={onClose} className="slide-panel-close">
        ✕
      </button>
      <div className="slide-panel-content">{children}</div>
    </div>
  );
};

const TransactionTableData = ({ data, getTransactions }: any) => {
  const navigate = useNavigate();
  const [cancellationReason, setCancellationReason] = useState<string>("");
  const [showCancelModal, setShowCancelModal] = useState<boolean>(false);
  const [showExtensionModal, setShowExtentionModal] = useState<boolean>(false);
  const [extensionLoading, setExtentionLoading] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(false);
  const [transactionId, setTransactionId] = useState<string>("");
  const [openIncident, setOpenIncident] = useState(false);
  const [initialDate, setInitialDates] = useState({
    arrivalDate: "",
    departureDate: "",
    refId: "",
    transactionStatus: "",
  });
  const [incident, setIncident] = useState("");
  const [extendedTransactions, setExtendedTransactions] = useState<any>([]);
  const [earlyCheckoutDisclaimer, setEarlyCheckoutDisclaimer] =
    useState<boolean>(false);
  const [slidePanelOpen, setSlidePanelOpen] = useState(false);
  const [selectedTransaction, _setSelectedTransaction] = useState<any>(null);
  const [openDropdownId, setOpenDropdownId] = useState<string | null>(null);
  const [menuPosition, setMenuPosition] = useState<MenuPosition | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  const [user] = useSessionStorage("user", {});

  const closeDropdown = () => {
    setOpenDropdownId(null);
    setMenuPosition(null);
    triggerRef.current = null;
  };

  const openDropdownAt = (id: string, button: HTMLButtonElement) => {
    const rect = button.getBoundingClientRect();
    const estimatedMenuHeight = 240;
    const spaceBelow = window.innerHeight - rect.bottom;
    const openUpward = spaceBelow < estimatedMenuHeight && rect.top > estimatedMenuHeight;

    triggerRef.current = button;
    setMenuPosition(
      openUpward
        ? {
            bottom: window.innerHeight - rect.top + 4,
            right: Math.max(8, window.innerWidth - rect.right),
          }
        : {
            top: rect.bottom + 4,
            right: Math.max(8, window.innerWidth - rect.right),
          },
    );
    setOpenDropdownId(id);
  };

  useEffect(() => {
    if (!openDropdownId) return;

    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (menuRef.current?.contains(target)) return;
      if (triggerRef.current?.contains(target)) return;
      closeDropdown();
    };

    const handleRepositionOrClose = () => closeDropdown();

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    window.addEventListener("resize", handleRepositionOrClose);
    window.addEventListener("scroll", handleRepositionOrClose, true);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
      window.removeEventListener("resize", handleRepositionOrClose);
      window.removeEventListener("scroll", handleRepositionOrClose, true);
    };
  }, [openDropdownId]);

  const handleManageBooking = (transaction: any) => {
    navigate(`/my-bookings/${transaction.referenceId}`);
  };

  const handleCancelTransaction = (reason: string) => {
    setLoading(true);

    cancelTransaction(transactionId, reason)
      .then(() => {
        setCancellationReason("");
        toast.success("Transactions successfully cancelled");
      })
      .catch((err) => {
        toast.error(err?.errors[0]?.message);

        setLoading(false);
      })
      .finally(() => {
        setLoading(false);
        setShowCancelModal(false);
        getTransactions();
      });
  };

  const handleSubmitIncident = (e: any) => {
    e.preventDefault();
    setLoading(true);
    reportIncident(transactionId, incident)
      .then((resp: any) => {
        ktoast.success(resp?.message);
      })
      .catch((err) => {
        ktoast.error(err.errors[0].message);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleCheckout = async (id: string) => {
    setLoading(true);
    const abortController = new AbortController();

    try {
      const res = await checkout(id, abortController.signal);
      toast.success((res as { message: string })?.message);
      setEarlyCheckoutDisclaimer(false);

      await getTransactions();
      navigate("/caution-fee");
    } catch (err) {
      const error = err as { errors: { message: string }[] };
      toast.error(error.errors[0].message);
    } finally {
      setLoading(false);
    }

    return () => {
      abortController.abort();
    };
  };
  useEffect(() => {
    if (!data || !Array.isArray(data)) return;

    // Backend will handle automatic cancellation when arrival date passes
    // const now = new Date().getTime();

    data.forEach((t: any) => {
      if (!t.arrival_date || !t.id) return;

      // const arrival = new Date(t.arrival_date);
      // const expiry = new Date(arrival);
      // expiry.setDate(expiry.getDate() + 1);
      // expiry.setHours(12, 0, 0, 0);

      // const expiryTime = expiry.getTime();

      // Backend will handle automatic cancellation when arrival date passes
      // if (
      //   now > expiryTime &&
      //   t?.listing?.intent !== "sale" &&
      //   !["cancelled", "completed"].includes(t.status)
      // ) {
      //   cancelTransaction(t.id, "Arrival date passed")
      //     .then(() => {
      //       toast.info(
      //         `Transaction ${t.referenceId} has been automatically cancelled because the arrival window passed.`,
      //       );
      //       getTransactions();
      //     })
      //     .catch((err) => {
      //       toast.error(err?.errors?.[0]?.message || "Failed to auto-cancel.");
      //     });
      // }
    });
  }, [data, getTransactions]);

  const handleExtendedTransactions = (id: string) => {
    const abortController = new AbortController();
    setExtentionLoading(true);
    getExtendedTransactions(id, user.id, abortController.signal)
      .then((d: any) => {
        setExtendedTransactions(d.data);
      })
      .catch(() => {})
      .finally(() => {
        setLoading(false);
        setExtentionLoading(false);
      });
  };

  return (
    <>
      <tbody className="list fs-base">
        <ToastContainer limit={1} />

        {data.map((info: any) => {
          const {
            arrival_date,
            createdAt,
            updatedAt,
            departure_date,
            isExtended,
            // inspection_date,

            id,
            owner,
            // type,
            // property_avalability_status,
            status,
            listing,
            hasCheckedOut,
            referenceId,
          } = info;
          const isDropdownOpen = openDropdownId === id;
          return (
            <tr key={id}>
              <td>
                <span className="item-booking-type text-primary capitalize fw-500 fs-10">
                  {info?.listing.instantBooking ? "Instant" : "non instant"}{" "}
                  booking
                </span>
              </td>
              <td>
                <span className="item-createdAt text-reset fs-13">
                  {new Date(createdAt?.toLocaleString()).toLocaleDateString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    },
                  )}
                </span>
              </td>
              <td>
                <Link
                  to={`/property/${listing?.slug}/`}
                  state={{ id: listing?.slug }}
                  className="text-black fs-13"
                >
                  {listing?.title.length > 0 &&
                    listing?.title.slice(0, 20) + "..."}
                </Link>
              </td>
              <td>
                <span className="item-intent fs-12">
                  {owner?.preferredName}
                </span>
              </td>

              <td>
                <span className="item-bedroom fs-13">
                  {listing?.intent === "sale"
                    ? "N/A"
                    : new Date(
                        arrival_date?.toLocaleString(),
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                </span>
              </td>
              <td>
                <span className="item-location fs-13">
                  {listing?.intent === "sale"
                    ? "N/A"
                    : new Date(
                        departure_date?.toLocaleString(),
                      ).toLocaleDateString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                </span>
              </td>

              <td>
                <span className="item-price fs-13">
                  {new Date(updatedAt?.toLocaleString()).toLocaleDateString(
                    "en-US",
                    {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    },
                  )}
                </span>
              </td>
              <td className="">
                {status === "customer-accepted" || status === "confirmed" ? (
                  <div
                    className="badge"
                    style={{ backgroundColor: "#d1fae5", color: "#065f46" }}
                  >
                    Confirmed
                  </div>
                ) : status === "completed" ? (
                  <div
                    className="badge"
                    style={{ backgroundColor: "#d1fae5", color: "#065f46" }}
                  >
                    Completed
                  </div>
                ) : status === "cancelled" ? (
                  <div
                    className="badge"
                    style={{ backgroundColor: "#fee2e2", color: "#991b1b" }}
                  >
                    Cancelled
                  </div>
                ) : status === "progress" ? (
                  <div
                    className="badge"
                    style={{ backgroundColor: "#dbeafe", color: "#1e40af" }}
                  >
                    In Progress
                  </div>
                ) : status === "pending_payment" ? (
                  <div
                    className="badge"
                    style={{ backgroundColor: "#e0f2fe", color: "#075985" }}
                  >
                    Pending Payment
                  </div>
                ) : status === "paid_pending_confirmation" ? (
                  <div
                    className="badge"
                    style={{ backgroundColor: "#fef3c7", color: "#92400e" }}
                  >
                    Pending Confirmation
                  </div>
                ) : status === "timeout_unconfirmed" ? (
                  <div
                    className="badge"
                    style={{ backgroundColor: "#fef3c7", color: "#92400e" }}
                  >
                    Timeout Unconfirmed
                  </div>
                ) : status === "awaiting-inspection" ? (
                  <div
                    className="badge"
                    style={{ backgroundColor: "#dbeafe", color: "#1e40af" }}
                  >
                    Awaiting Inspection
                  </div>
                ) : (
                  <div
                    className="badge"
                    style={{ backgroundColor: "#f3f4f6", color: "#374151" }}
                  >
                    {formatStatusLabel(status) || status}
                  </div>
                )}
              </td>
              {listing?.intent === "long lease" ? (
                <>
                  <td className="">
                    {/* <Link to={`/message/${id}`} className=" fs-13">
                Manage Booking
              </Link> */}
                    <div
                      className="text-primary fw-bold fs-12 cursor-pointer"
                      onClick={() => handleManageBooking(info)}
                    >
                      Preview property
                    </div>
                  </td>
                  <td className="">
                    <button
                      disabled={status === "completed"}
                      className={`dropdown-item btn fw-bold btn-link fs-13 border-input border-round-xl p-1 text-center ${
                        status === "completed" ? "text-white" : "text-red"
                      }`}
                      onClick={() => {
                        setShowCancelModal(true);
                        setTransactionId(id);
                      }}
                    >
                      X &nbsp;Close Transaction
                    </button>
                  </td>
                </>
              ) : (
                <td className="">
                  <div className="dropdown">
                    <button
                      className="dropdown-ellipsis btn btn-link"
                      id="dropdownMenuButton1"
                      type="button"
                      aria-expanded={isDropdownOpen}
                      onClick={(e) => {
                        if (isExtended) {
                          handleExtendedTransactions(id);
                          setTransactionId(id);
                          setInitialDates({
                            ...initialDate,
                            arrivalDate: arrival_date,
                            departureDate: departure_date,
                            refId: referenceId,
                            transactionStatus: status,
                          });
                        }
                        if (openDropdownId === id) {
                          closeDropdown();
                        } else {
                          openDropdownAt(id, e.currentTarget);
                        }
                      }}
                    >
                      <i className="fa-solid fa-ellipsis-vertical text-primary"></i>
                    </button>

                    {isDropdownOpen &&
                      menuPosition &&
                      createPortal(
                        <div
                          ref={menuRef}
                          className="dropdown-menu show booking-actions-menu"
                          style={{
                            position: "fixed",
                            zIndex: 1050,
                            left: "auto",
                            ...menuPosition,
                          }}
                        >
                        <div className="dropdown-header fs-13 fw-bold">
                          Actions
                        </div>
                        <Link
                          to={`/my-bookings/${id}`}
                          className="dropdown-item fs-13"
                          onClick={closeDropdown}
                        >
                          Preview property
                        </Link>
                        {listing?.intent === "shortlet" &&
                          (status === "confirmed" ||
                            (status === "completed" && hasCheckedOut)) && (
                            <>
                              {status === "completed" && hasCheckedOut ? (
                                <p className="fs-14 text-primary m-0 dropdown-item">
                                  You have successful Checked out
                                </p>
                              ) : (
                                <div>
                                  {(() => {
                                    const currentTime = new Date().getTime();
                                    const arrivalTime = new Date(
                                      arrival_date,
                                    ).getTime();
                                    const departureTime = new Date(
                                      departure_date,
                                    ).getTime();

                                    const hasValidFutureExtension =
                                      isExtended &&
                                      extendedTransactions?.some(
                                        (trans: any) =>
                                          new Date(
                                            trans.arrival_date,
                                          ).getTime() > currentTime,
                                      );

                                    if (hasValidFutureExtension) {
                                      return null;
                                    }

                                    if (
                                      currentTime >= arrivalTime &&
                                      currentTime < departureTime
                                    ) {
                                      return (
                                        <span
                                          className="fs-13 dropdown-item cursor-pointer"
                                          onClick={() => {
                                            setEarlyCheckoutDisclaimer(true);
                                            setTransactionId(id);
                                            closeDropdown();
                                          }}
                                        >
                                          Request caution fee
                                        </span>
                                      );
                                    } else if (
                                      currentTime === departureTime ||
                                      departureTime < currentTime
                                    ) {
                                      return (
                                        <span
                                          className="fs-13 dropdown-item cursor-pointer"
                                          onClick={() => {
                                            setEarlyCheckoutDisclaimer(true);
                                            setTransactionId(id);
                                            closeDropdown();
                                          }}
                                        >
                                          Checkout from shortlet
                                        </span>
                                      );
                                    }

                                    return null;
                                  })()}
                                </div>
                              )}
                              {isExtended && (
                                <div>
                                  <button
                                    className="dropdown-item fs-13"
                                    type="button"
                                    onClick={() => {
                                      setShowExtentionModal(true);
                                      closeDropdown();
                                    }}
                                  >
                                    View Extensions
                                  </button>
                                  <ul className="dropdown-menu">
                                    {extendedTransactions?.map((trans: any) => (
                                      <li key={trans.id}>
                                        <p className="fs-14 cursor-pointer dropdown-item">
                                          {new Date(
                                            trans?.arrival_date?.toLocaleString(),
                                          ).toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                          })}
                                          {" - "}
                                          {new Date(
                                            trans?.departure_date?.toLocaleString(),
                                          ).toLocaleDateString("en-US", {
                                            year: "numeric",
                                            month: "short",
                                            day: "numeric",
                                          })}
                                          {" ("}
                                          {differenceInDays(
                                            new Date(trans?.departure_date),
                                            new Date(trans?.arrival_date),
                                          )}
                                          {trans.listing.intent === "shortlet"
                                            ? " night)"
                                            : " days)"}
                                        </p>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              )}
                            </>
                          )}

                        {status === "progress" && (
                          <button
                            className="dropdown-item btn btn-link fs-13 text-danger"
                            onClick={() => {
                              setShowCancelModal(true);
                              setTransactionId(id);
                              closeDropdown();
                            }}
                          >
                            Cancel Transaction
                          </button>
                        )}
                        {listing.intent === "long lease" &&
                          status === "completed" && (
                            <button
                              className="dropdown-item btn btn-link fs-13"
                              onClick={() => {
                                setOpenIncident(true);
                                setTransactionId(id);
                                closeDropdown();
                              }}
                            >
                              Report a problem
                            </button>
                          )}
                      </div>,
                      document.body,
                    )}
                  </div>
                </td>
              )}
            </tr>
          );
        })}

        <Modal
          center
          open={showCancelModal}
          onClose={() => {
            setShowCancelModal(false);
            getTransactions();
          }}
          classNames={{
            modalAnimationIn: "customEnterModalAnimation",
            modalAnimationOut: "customLeaveModalAnimation",
            modal: "customModal",
          }}
          animationDuration={500}
          aria-labelledby="summary-title"
          aria-describedby="summary-description"
        >
          <div className="col-12 mt-5 ">
            <h6 className="fs-12 fw-bold h-modal-color">Cancel Transaction</h6>
            <p className="fs-14 light-text" id="summary-description">
              We are sorry you have to cancel this transaction. In other to
              serve you better please share with us the reason why you are
              cancelling this transaction.
            </p>
            <form
              className="mt-4 mt-md-3"
              onSubmit={(e) => {
                e.preventDefault();
                handleCancelTransaction(cancellationReason);
              }}
            >
              <div className="col mb-4">
                <textarea
                  className="form-control border-round-xl py-3 px-4 fs-14 bg-purple"
                  required
                  placeholder="Please state your reason"
                  rows={4}
                  maxLength={500}
                  value={cancellationReason}
                  onChange={(e: any) => setCancellationReason(e.target.value)}
                ></textarea>
              </div>

              <div className="col text-center">
                <PrimaryButton type="primary" text="Submit" loading={loading} />
              </div>
            </form>
          </div>
        </Modal>
        {/* Extesion modal */}
        <Modal
          center
          open={showExtensionModal}
          onClose={() => {
            setShowExtentionModal(false);
          }}
          classNames={{
            modalAnimationIn: "customEnterModalAnimation",
            modalAnimationOut: "customLeaveModalAnimation",
            modal: "customModal",
          }}
          animationDuration={500}
          aria-labelledby="summary-title"
          aria-describedby="summary-description"
        >
          <div className="col-12 mt-5 ">
            <h6 className="fs-16 fw-bold h-modal-color mb-2">
              Booking History
            </h6>
            <div>
              <div>
                <h6 className="fs-13 fw-bold h-modal-color">Initial Booking</h6>
                <p className="fs-14 cursor-pointer ">
                  {new Date(
                    initialDate.arrivalDate?.toLocaleString(),
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                  {" - "}
                  {new Date(
                    initialDate.departureDate?.toLocaleString(),
                  ).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "short",
                    day: "numeric",
                  })}
                  {" ("}
                  {differenceInDays(
                    new Date(initialDate.departureDate),
                    new Date(initialDate.arrivalDate),
                  )}
                  night {")"}
                </p>
                <span className="fs-10 mr-1">
                  Ref ID : {initialDate?.refId}{" "}
                </span>{" "}
                <>
                  <span className="fs-10">Status : </span>
                  <span
                    className={`fs-10 ${
                      initialDate.transactionStatus === "completed"
                        ? "text-success"
                        : "text-danger"
                    } `}
                  >
                    {initialDate.transactionStatus}{" "}
                  </span>
                </>
              </div>

              <h6 className="fs-13 fw-bold h-modal-color mt-3">Extensions</h6>

              {extensionLoading ? (
                <span>Loading...</span>
              ) : (
                <ul className="">
                  {extendedTransactions?.map((trans: any) => {
                    return (
                      <li key={trans.id} className="mb-2">
                        <p className="fs-14 cursor-pointer ">
                          {new Date(
                            trans?.arrival_date?.toLocaleString(),
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                          {" - "}
                          {new Date(
                            trans?.departure_date?.toLocaleString(),
                          ).toLocaleDateString("en-US", {
                            year: "numeric",
                            month: "short",
                            day: "numeric",
                          })}
                          {" ("}
                          {differenceInDays(
                            new Date(trans?.departure_date),
                            new Date(trans?.arrival_date),
                          )}
                          {trans.listing.intent === "shortlet"
                            ? " night)"
                            : " days)"}
                        </p>
                        <span className="fs-10 mr-1">
                          Ref ID : {trans.referenceId}{" "}
                        </span>{" "}
                        <>
                          <span className="fs-10">Status : </span>
                          <span
                            className={`fs-10 ${
                              trans.status === "completed"
                                ? "text-success"
                                : "text-danger"
                            } `}
                          >
                            {formatStatusLabel(trans.status) || trans.status}{" "}
                          </span>
                        </>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          </div>
        </Modal>
        <Modal
          open={openIncident}
          center
          onClose={() => setOpenIncident(false)}
          // classNames={{ modal: 'w-300 rounded' }}
          classNames={{
            // overlayAnimationIn: 'customEnterOverlayAnimation',
            // overlayAnimationOut: 'customLeaveOverlayAnimation',
            modalAnimationIn: "customEnterModalAnimation",
            modalAnimationOut: "customLeaveModalAnimation",
            modal: "customModal",
          }}
          animationDuration={800}
        >
          <div className="col-10 mt-3">
            <div className="col-12">
              <h6 className="fs-12 fw-bold h-modal-color">
                Report an Incident
              </h6>
              <p className="fs-14 light-text" id="summary-description">
                If you have any complaint or encounter any challenges with the
                Agent or Landlord please report it we will be happy to help
                resolve it.
              </p>
              <form className="mt-4 mt-md-3" onSubmit={handleSubmitIncident}>
                <div className="col mb-4">
                  <textarea
                    name="incident"
                    id="incident"
                    placeholder="Please state your complaint"
                    value={incident}
                    onChange={(e: any) => setIncident(e.target.value)}
                    required
                    className="w-100 p-3"
                    rows={5}
                  ></textarea>
                </div>

                <div className="col text-center">
                  <PrimaryButton
                    type="primary"
                    text="Submit"
                    loading={loading}
                  />
                </div>
              </form>
            </div>
          </div>
        </Modal>
        <Modal
          open={earlyCheckoutDisclaimer}
          center
          onClose={() => {
            if (!loading) setEarlyCheckoutDisclaimer(false);
          }}
          showCloseIcon={false}
          animationDuration={200}
          classNames={{
            overlay: "disclaimer-overlay",
            modal: "disclaimer-modal",
          }}
        >
          <div className="disclaimer-content">
            <div className="disclaimer-icon">
              <svg
                width="22"
                height="22"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"
                />
              </svg>
            </div>
            <h6 className="disclaimer-title">Early checkout</h6>
            <p className="disclaimer-description">
              Early checkout may result in no refund, except in cases of
              property faults or unforeseen events which depends on host
              cancellation policy, circumstance, and Krent evaluation. Contact
              us within 24 hours to resolve any disputes.
            </p>
            <div className="disclaimer-actions">
              <button
                type="button"
                className="disclaimer-cancel-btn"
                onClick={() => setEarlyCheckoutDisclaimer(false)}
                disabled={loading}
              >
                Cancel
              </button>
              <button
                type="button"
                className="disclaimer-confirm-btn"
                onClick={() => handleCheckout(transactionId)}
                disabled={loading}
              >
                {loading ? "Checking out..." : "Checkout"}
              </button>
            </div>
          </div>
        </Modal>
      </tbody>
      <SlidePanel
        isOpen={slidePanelOpen}
        onClose={() => setSlidePanelOpen(false)}
      >
        {selectedTransaction && (
          <div className="mt-3 mt-md-0">
            <div className="flex justify-between items-center mt-5">
              <Link
                to={`/messaging/${selectedTransaction.id}`}
                className="dropdown-item fs-13 bg-primary rounded-50 w-max text-white fw-bold py-2 px-4"
                state={{ from: "external" }}
              >
                Go to messaging
              </Link>
              <button
                className="bg-white font-bold d-md-none "
                onClick={() => setSlidePanelOpen(false)}
              >
                <i className="fa fa-x  fs-14 p-2  rounded-pill"></i>
              </button>
            </div>

            <div className="text-center mt-4">
              <img
                src={
                  "https://aui.atlassian.com/aui/8.8/docs/images/avatar-person.svg"
                }
                alt=""
                width={100}
                className="rounded-circle"
              />
              <p className="uppercase fw-bold my-2">
                {selectedTransaction.owner?.preferredName}
              </p>

              <h6 className="fw-bold my-2">
                {selectedTransaction?.sent_by?.name}
              </h6>
            </div>
            <div className="text-start mt-5 mb-4">
              <h6 className="fs-12 fw-bold m-0">REQUEST TYPE</h6>
              <p className="fs-14 m-0">
                {selectedTransaction?.listing?.intent}
              </p>
            </div>
            {selectedTransaction?.listing?.intent !== "sale" &&
              selectedTransaction &&
              Object.keys(selectedTransaction).length !== 0 && (
                <div className="text-start mb-4">
                  <h6 className="fs-12 fw-bold m-0">DURATION</h6>
                  <p className="fs-14 m-0">
                    {new Date(
                      selectedTransaction?.arrival_date?.toLocaleString(),
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                    {" - "}
                    {new Date(
                      selectedTransaction?.departure_date?.toLocaleString(),
                    ).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "short",
                      day: "numeric",
                    })}
                    {" ("}
                    {differenceInDays(
                      new Date(selectedTransaction?.departure_date),
                      new Date(selectedTransaction?.arrival_date),
                    )}{" "}
                    {selectedTransaction.listing.intent === "shortlet"
                      ? " night)"
                      : " days)"}
                  </p>
                </div>
              )}
            <div className="text-start  mb-4">
              <h6 className="fs-12 fw-bold m-0">Transaction Reference</h6>
              <p className="fs-14 m-0">{selectedTransaction?.referenceId}</p>
            </div>

            {/* Booking Details */}
            <div className="space-y-4">
              {selectedTransaction &&
                selectedTransaction?.status &&
                (selectedTransaction?.status === "progress" ||
                  selectedTransaction?.status === "awaiting-inspection") &&
                selectedTransaction?.inspection_date &&
                !isEmpty(selectedTransaction?.inspection_schedules) && (
                  <div className="alert alert-primary" role="alert">
                    <p className="fs-14">
                      Inspection Date has been scheduled for
                      {new Date(
                        selectedTransaction?.inspection_date?.toLocaleString(),
                      ).toLocaleTimeString("en-US", {
                        year: "numeric",
                        month: "short",
                        day: "numeric",
                      })}
                    </p>
                  </div>
                )}
            </div>
          </div>
        )}
      </SlidePanel>
    </>
  );
};

export default TransactionTableData;
